import {useEffect, useState} from 'react'
import {Link, useSearchParams, useNavigate} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {verifyEmail, resendVerificationEmail} from '../core/_requests'
import {Toast} from '../../../../_metronic/partials/widgets'
import {AuthAuroraShell, AuthBrandHeader} from './AuthAurora'

export function VerifyEmail() {
  const intl = useIntl()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [verified, setVerified] = useState<boolean | null>(null)
  const [alreadyVerified, setAlreadyVerified] = useState(false)
  const [message, setMessage] = useState('')
  const [resending, setResending] = useState(false)

  useEffect(() => {
    const token = searchParams.get('token')
    const email = searchParams.get('email')

    if (!token || !email) {
      setVerified(false)
      setLoading(false)
      setMessage('Link de verificação inválido. Faltam parâmetros necessários.')
      Toast.fire({
        icon: 'error',
        title: 'Link inválido',
        text: 'O link de verificação está incompleto ou inválido.',
        customClass: {
          popup: 'bg-light-danger',
          title: 'text-danger',
        },
      })
      return
    }

    // Call API to verify email
    verifyEmail(token, email)
      .then((response) => {
        if (response.data.success) {
          setVerified(true)
          const isAlreadyVerified = response.data.already_verified === true
          setAlreadyVerified(isAlreadyVerified)
          setMessage(response.data.message || 'Email verificado com sucesso!')

          if (isAlreadyVerified) {
            // Email already verified - show info message
            Toast.fire({
              icon: 'warning',
              title: 'Email já verificado',
              text: 'Este email já foi verificado anteriormente. Este link de verificação já foi utilizado.',
              customClass: {
                popup: 'bg-light-warning',
                title: 'text-warning',
              },
            })
            // Redirect to login after 3 seconds
            setTimeout(() => {
              navigate('/auth/login')
            }, 3000)
          } else {
            // Email just verified - show success message
            Toast.fire({
              icon: 'success',
              title: 'Email verificado com sucesso!',
              text: 'Sua conta foi ativada. Agora você pode fazer login.',
              customClass: {
                popup: 'bg-light-success',
                title: 'text-success',
              },
            })
            // Redirect to login after 3 seconds
            setTimeout(() => {
              navigate('/auth/login')
            }, 3000)
          }
        } else {
          setVerified(false)
          setAlreadyVerified(false)
          setMessage(response.data.message || 'Erro ao verificar email.')
        }
      })
      .catch((error) => {
        setVerified(false)
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Erro ao verificar email. Tente novamente mais tarde.'
        setMessage(errorMessage)

        let toastTitle = 'Erro na verificação'
        let toastText = errorMessage

        if (error?.response?.status === 400) {
          if (error?.response?.data?.error === 'token_not_found') {
            toastTitle = 'Token não encontrado'
            toastText = 'Token de verificação não encontrado. Solicite um novo link de verificação.'
          } else {
            toastTitle = 'Token inválido ou expirado'
            toastText =
              'Este link de verificação expirou ou já foi utilizado. Solicite um novo link.'
          }
        } else if (error?.response?.status === 404) {
          toastTitle = 'Usuário não encontrado'
          toastText = 'Não foi possível encontrar uma conta com este email.'
        }

        Toast.fire({
          icon: 'error',
          title: toastTitle,
          text: toastText,
          customClass: {
            popup: 'bg-light-danger',
            title: 'text-danger',
          },
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }, [searchParams, navigate])

  const handleResendVerification = async () => {
    const email = searchParams.get('email')
    if (!email) {
      Toast.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Email não encontrado. Por favor, acesse o link novamente.',
        customClass: {
          popup: 'bg-light-danger',
          title: 'text-danger',
        },
      })
      return
    }

    setResending(true)
    try {
      await resendVerificationEmail(email)
      Toast.fire({
        icon: 'success',
        title: 'Email reenviado!',
        text: 'Um novo link de verificação foi enviado para seu email.',
        customClass: {
          popup: 'bg-light-success',
          title: 'text-success',
        },
      })
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Erro ao reenviar email. Tente novamente mais tarde.'
      Toast.fire({
        icon: 'error',
        title: 'Erro ao reenviar',
        text: errorMessage,
        customClass: {
          popup: 'bg-light-danger',
          title: 'text-danger',
        },
      })
    } finally {
      setResending(false)
    }
  }

  return (
    <AuthAuroraShell>
      <AuthBrandHeader />

      {/*begin::Verification Status*/}
      <div className='text-center mb-8'>
        <h1 className='text-dark fw-bolder mb-3'>Verificação de Email</h1>
        <div className='text-gray-500 fw-semibold fs-6'>
          Aguarde enquanto verificamos seu email...
        </div>
      </div>

      {loading && (
        <div className='text-center'>
          <div className='spinner-border spinner-border-lg' role='status'>
            <span className='visually-hidden'>Carregando...</span>
          </div>
          <div className='mt-5 text-gray-600'>Verificando seu email...</div>
        </div>
      )}

      {!loading && verified === true && alreadyVerified && (
        <div className='mb-10'>
          <div className='mb-lg-15 alert alert-warning'>
            <div className='alert-text font-weight-bold'>
              <i className='bi bi-info-circle-fill me-2'></i>
              {message}
            </div>
          </div>
          <div className='text-center text-gray-600 mb-5'>
            Você já pode fazer login. Será redirecionado em alguns segundos...
          </div>
          <div className='text-center'>
            <Link to='/auth/login'>
              <button type='button' className='btn btn-primary'>
                Ir para Login
              </button>
            </Link>
          </div>
        </div>
      )}

      {!loading && verified === true && !alreadyVerified && (
        <div className='mb-10'>
          <div className='mb-lg-15 alert alert-success'>
            <div className='alert-text font-weight-bold'>
              <i className='bi bi-check-circle-fill me-2'></i>
              {message}
            </div>
          </div>
          <div className='text-center text-gray-600 mb-5'>
            Você será redirecionado para a página de login em alguns segundos...
          </div>
          <div className='text-center'>
            <Link to='/auth/login'>
              <button type='button' className='btn btn-primary'>
                Ir para Login
              </button>
            </Link>
          </div>
        </div>
      )}

      {!loading && verified === false && (
        <div className='mb-10'>
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>
              <i className='bi bi-exclamation-triangle-fill me-2'></i>
              {message}
            </div>
          </div>
          <div className='text-center'>
            <Link to='/auth/login'>
              <button type='button' className='btn btn-primary me-3'>
                Ir para Login
              </button>
            </Link>
            <button
              type='button'
              className='btn btn-light'
              onClick={handleResendVerification}
              disabled={resending}
            >
              {resending ? (
                <>
                  <span
                    className='spinner-border spinner-border-sm me-2'
                    role='status'
                    aria-hidden='true'
                  ></span>
                  Enviando...
                </>
              ) : (
                'Solicitar Novo Link'
              )}
            </button>
          </div>
        </div>
      )}
      {/*end::Verification Status*/}
    </AuthAuroraShell>
  )
}
